import { Button, ButtonDropdown, Dialog, Spinner } from '@rio-cloud/rio-uikit';
import { useNotificationUsers } from '../../../../../hooks/useNotificationUsers';
import NotificationUserForm from '../form/NotificationUserForm';
import { RestrictArea } from '../../../../../components/restrictArea';

const NotificationUserListContent = () => {
  const {
    notificationUsers,
    loading,
    deleteLoading,
    error,
    showForm,
    showDeleteDialog,
    notificationUserToDelete,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    addNotificationUser,
    userHasAccess,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    handlePageChange,
  } = useNotificationUsers();

  if (loading || userHasAccess === null) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!userHasAccess) {
    return <RestrictArea showBackButton={false} />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600 text-center">
          <p className="text-lg font-semibold mb-2">Erro ao carregar usuários a serem notificados</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1000px] mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="text-size-h2">Usuários a serem notificados</div>
        </div>
        <Button
          bsStyle={Button.PRIMARY}
          onClick={() => setShowForm(true)}
          iconName="rioglyph-plus-sign"
        >
          Novo Usuário
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-gray-500">
                  Nome
                </th>
                <th className="px-6 py-3 text-left font-medium text-gray-500">
                  Email
                </th>
                <th className="px-6 py-3 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notificationUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                    <div className="flex justify-end space-x-2">
                      <ButtonDropdown
                        title={<span className='rioglyph rioglyph-option-vertical' />}
                        items={[
                          {
                            value: 'Excluir',
                            onSelect: () => handleDelete(user)
                          }
                        ]}
                        bsStyle='muted'
                        pullRight
                        noCaret
                        usePortal
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {notificationUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <p className="font-medium mb-2">Nenhum usuário encontrado</p>
            </div>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Próxima
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> até{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalElements)}
                </span>{' '}
                de <span className="font-medium">{totalElements}</span> resultados
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  bsStyle={Button.DEFAULT}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    return (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;
                    
                    return (
                      <span key={page}>
                        {showEllipsis && (
                          <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            page === currentPage
                              ? 'z-10 bg-blue-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                          }`}
                        >
                          {page}
                        </button>
                      </span>
                    );
                  })}
                <Button
                  bsStyle={Button.DEFAULT}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <Dialog
          show={showForm}
          onClose={handleFormClose}
          showCloseButton={true}
          title="Novo Usuário"
          bsSize={Dialog.SIZE_SM}
          body={(
            <NotificationUserForm
              addNotificationUser={addNotificationUser}
              onSuccess={() => {
                handleFormSuccess();
              }}
              onCancel={() => {
                handleFormClose();
              }}
            />
          )}
        />
      )}

      <Dialog
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Confirmar Exclusão"
        bsSize={Dialog.SIZE_SM}
      >
        <div className="p-6">
          <p className="text-size-h4 text-gray-700 mb-4">
            Tem certeza que deseja excluir o usuário{' '}
            <strong>
              {notificationUserToDelete?.firstName} {notificationUserToDelete?.lastName}
            </strong>?
          </p>
          <p className="text-gray-600 mb-6">
            Esta ação não pode ser desfeita.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              bsStyle={Button.DEFAULT}
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              bsStyle={Button.DANGER}
              onClick={confirmDelete}
              disabled={deleteLoading}
              iconName={deleteLoading ? "rioglyph-spinner" : undefined}
            >
              {deleteLoading ? "Excluindo..." : "Excluir"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

const NotificationUserList = (props: any) => {
  return (
    <NotificationUserListContent {...props} />
  );
};

export default NotificationUserList;

